import * as Sentry from "@sentry/react";
import type {
  TIntakeDefinition,
  TIntakeForm,
  TIntakePage,
  TIntakeSubmission,
} from "@/types/intake";

/**
 * The single boundary that turns an untrusted, API-served intake ``definition``
 * blob into a structurally valid {@link TIntakeDefinition} — and reports every
 * repair it has to make (#3760).
 *
 * **Why a boundary and not per-site guards.** A definition arriving without a
 * ``pages`` array white-screened the app in the 2026-07-28 prod incident: six
 * distinct Sentry issues, every one an unguarded ``definition.pages``
 * dereference (transform-to-survey, strip-system-fields, plan-work-data,
 * intake-renderer, the store's ``hydrateFromForm`` ×2). #3633 shipped per-site
 * ``?? []`` guards and the crashes simply *moved* on the very next release, to
 * sites the patch hadn't touched — there are ~16 such dereferences across 13
 * files, and which one blows up is only a function of which page the user
 * opened. So the contract is enforced once, where the data *enters* the FE
 * (``IntakeService`` / ``FormWorkflowService``), and every read site downstream
 * can trust ``pages`` and ``page.elements`` to be arrays.
 *
 * **Why it reports.** The ``?? []`` guards were, in one respect, worse than the
 * crash: they rendered a silently-empty form and produced no Sentry issue at
 * all — the corrupt prod data was still being served the next morning, and the
 * *crash* is what told us. So a repair is never silent: it raises one
 * fingerprinted Sentry error (see {@link reportIntakeDefinitionRepair}) and a
 * ``console.error``. The BE root cause is tracked separately in #3634; this
 * module is the FE's half — fail loudly, degrade visibly.
 *
 * **Referential identity matters.** A valid definition is returned *as the same
 * object reference*, never cloned: many consumers are ``useMemo``-keyed on the
 * definition and MobX-observed, so cloning on every call would break
 * memoization and churn renders. Allocation happens only when repairing, and
 * even then untouched pages are reused by reference.
 */

/**
 * What normalization had to repair. The reported one is the Sentry fingerprint.
 *
 * Split by whether the shape is **unambiguously corrupt** or merely **empty**.
 * That distinction is the difference between signal and noise here, because an
 * empty definition is a perfectly normal state on some paths:
 * `IntakeForm.definition` is `models.JSONField(default=dict)`
 * (`intake/models/intake.py`) and `IntakeFormSerializer.create` never populates
 * it, so **every brand-new form serializes as `{}`** — and
 * `resolve_read_definition` (`intake/definition_render.py`) can likewise return
 * a bare `{}`. The builder is explicitly designed for it
 * (`injectMissingDefaultSystemFields` opens with `if (!definition.pages?.length)
 * return definition`). Alarming on that would make `createForm` / `getForm` /
 * `listForms` fire errors on an everyday user action, drown the real signal, and
 * get the alert muted — the exact failure this ticket exists to prevent.
 *
 * But the incident payload was *also* effectively `{}` (the legacy blob emptied
 * by instantiate per #3417, snapshot reconstruction failing per #3634), so
 * suppressing "empty" wholesale would have missed the incident entirely. The two
 * are indistinguishable from the definition object alone — which is why the
 * caller must declare a {@link TIntakeDefinitionExpectation}.
 */
export type TIntakeDefinitionRepair =
  // ── Unambiguously corrupt: a real bug on every path, always reported. ──
  /** The payload was present but not an object (string / number / array). */
  | "definition_not_object"
  /** ``pages`` was present but not an array — the shape behind the prod crashes. */
  | "pages_not_array"
  /** An entry in ``pages`` wasn't an object. */
  | "page_not_object"
  /** A page's ``elements`` was missing or not an array. */
  | "page_elements_not_array"
  /** ``version`` was present but not the schema's ``1``. */
  | "version_invalid"
  // ── Ambiguous: reported only where an empty definition can't be legitimate. ──
  /** No content at all — ``{}``, ``pages`` absent, or a null/absent definition. */
  | "definition_empty"
  /** ``version`` was simply absent. Defaulted; never worth an alert on its own —
   *  a schema tag's absence has never crashed anything, and `{}` (the normal new
   *  form) would otherwise always trip it. */
  | "version_missing";

/**
 * Whether an empty definition is a legitimate state at this entry point. The
 * caller declares it truthfully; it is the single input that decides whether
 * ``definition_empty`` alarms, so it is deliberately explicit rather than
 * inferred by a heuristic inside this module.
 *
 *  - ``"requires-definition"`` — a definition MUST be there (an existing request
 *    was filled against one; a published form advertises content). Empty ⇒ report.
 *  - ``"may-be-empty"`` — empty is a normal state (an unauthored new form, an
 *    author previewing a draft graph). Empty ⇒ normalize silently.
 *
 * Structural corruption is reported under either value.
 */
type TIntakeDefinitionExpectation = "requires-definition" | "may-be-empty";

/** Repairs that never alarm on their own — see {@link TIntakeDefinitionRepair}. */
const NEVER_REPORTED: readonly TIntakeDefinitionRepair[] = ["version_missing"];

/** Repairs that alarm only under ``"requires-definition"``. */
const AMBIGUOUS_REPAIRS: readonly TIntakeDefinitionRepair[] = ["definition_empty"];

/**
 * Most- to least-severe. The first *reportable* repair in this order becomes the
 * reported ``reason`` (and therefore the Sentry fingerprint), so one malformed
 * payload that trips several checks stays a single, stably-grouped issue.
 */
const REPAIR_SEVERITY: readonly TIntakeDefinitionRepair[] = [
  "definition_not_object",
  "pages_not_array",
  "page_not_object",
  "page_elements_not_array",
  "version_invalid",
  "definition_empty",
  "version_missing",
];

/** The schema version every served definition is expected to carry. */
const DEFINITION_VERSION = 1;

/** Where the definition entered the FE — a service method name, for triage. */
export interface TIntakeDefinitionContext {
  /** The `IntakeService` / `FormWorkflowService` method the payload came from. */
  source: string;
  /** Whether an empty definition is legitimate here. See the type's docs. */
  expectation: TIntakeDefinitionExpectation;
  /** The intake form id, when the caller knows it. Never fingerprinted. */
  formId?: string | null;
  /** The workspace slug, when the caller knows it. Never fingerprinted. */
  workspaceSlug?: string | null;
}

/**
 * Whether ``reason`` is worth a Sentry error given what the caller expects.
 * Structural corruption always is; an ambiguous empty only where empty can't be
 * legitimate; a bare missing ``version`` never is.
 */
function isReportableRepair(
  reason: TIntakeDefinitionRepair,
  expectation: TIntakeDefinitionExpectation,
): boolean {
  if (NEVER_REPORTED.includes(reason)) return false;
  if (AMBIGUOUS_REPAIRS.includes(reason)) return expectation === "requires-definition";
  return true;
}

/** What {@link reportIntakeDefinitionRepair} is handed. Shape only — never values. */
interface TIntakeDefinitionRepairReport extends TIntakeDefinitionContext {
  /** The most severe repair, used as the Sentry fingerprint discriminator. */
  reason: TIntakeDefinitionRepair;
  /** Every distinct repair applied, most severe first. */
  repairs: TIntakeDefinitionRepair[];
  /** ``typeof`` the offending payload — shape, not content. */
  definitionType: string;
  /** ``typeof`` the offending ``pages`` ("array" when it was one). */
  pagesType: string;
  /** How many pages survived normalization. */
  pageCount: number;
}

/** Pure result of {@link inspectIntakeDefinition} — no reporting, no globals. */
export interface TIntakeDefinitionInspection {
  /** A structurally valid definition; the input itself when nothing was wrong. */
  definition: TIntakeDefinition;
  /** Empty when the input was already valid. */
  repairs: TIntakeDefinitionRepair[];
}

/**
 * The Sentry error message. Stable prefix + the repair reason, so the issue
 * title reads actionably while grouping stays driven by the explicit
 * fingerprint below.
 */
function intakeDefinitionRepairMessage(reason: TIntakeDefinitionRepair): string {
  return `Malformed intake form definition repaired at the FE boundary: ${reason}`;
}

/**
 * Explicit fingerprint keyed on the *repair reason* only — never on a form id,
 * workspace slug or route. An explicit array replaces Sentry's default
 * stack-based grouping, which is the whole point: the same corrupt blob is read
 * from ~16 call sites across 13 files, so default grouping fans one data fault
 * out into an issue per crash site (exactly what the 2026-07-28 incident did).
 * ``{{ default }}`` is deliberately omitted — including it would let the call
 * stack re-split the buckets — mirroring ``axiosFingerprint`` in
 * ``services/sentry-capture-policy.ts``.
 */
export function intakeDefinitionRepairFingerprint(reason: TIntakeDefinitionRepair): string[] {
  return ["intake-definition-malformed", reason];
}

/**
 * Distinct ``reason|formId`` pairs already reported this session. A malformed
 * definition is read on every MobX-driven re-render, so without this a single
 * corrupt form could emit hundreds of identical events. The **first**
 * occurrence of each pair always reports.
 */
const reportedRepairs = new Set<string>();

/**
 * Cap on {@link reportedRepairs}. Past this the set stops growing and further
 * repairs downgrade to a ``console.warn`` (still forwarded to Sentry Logs by
 * ``consoleLoggingIntegration``), so the dedupe can't become an unbounded leak
 * and a pathological session can't become an unbounded event flood.
 */
const MAX_TRACKED_REPAIRS = 200;

/** Reset the session dedupe. Test-only seam. */
export function resetIntakeDefinitionRepairDedupe(): void {
  reportedRepairs.clear();
}

/**
 * Raise one fingerprinted Sentry error + a ``console.error`` for a repair.
 *
 * Deliberately carries **no definition content** — no page/element names, no
 * answers, no raw blob. Intake definitions and their labels are customer data,
 * and `sendDefaultPii` is off by design (see docs/operations/observability.md),
 * so this describes *shape*: the repair reason, the offending ``typeof``s, the
 * surviving page count, plus the ids needed to go look at the record.
 */
function reportIntakeDefinitionRepair(report: TIntakeDefinitionRepairReport): void {
  const context = {
    reason: report.reason,
    repairs: report.repairs.join(","),
    source: report.source,
    formId: report.formId ?? null,
    workspaceSlug: report.workspaceSlug ?? null,
    definitionType: report.definitionType,
    pagesType: report.pagesType,
    pageCount: report.pageCount,
  };

  const dedupeKey = `${report.reason}|${report.formId ?? "unknown"}`;
  if (reportedRepairs.has(dedupeKey)) return;
  if (reportedRepairs.size >= MAX_TRACKED_REPAIRS) {
    console.warn(
      "[intake] malformed definition repaired (report cap reached, not captured)",
      context,
    );
    return;
  }
  reportedRepairs.add(dedupeKey);

  const error = new Error(intakeDefinitionRepairMessage(report.reason));
  console.error("[intake] malformed form definition repaired at the FE boundary", context, error);
  Sentry.captureException(error, {
    level: "error",
    fingerprint: intakeDefinitionRepairFingerprint(report.reason),
    tags: {
      source: "intake-definition",
      reason: report.reason,
      definition_type: report.definitionType,
      pages_type: report.pagesType,
    },
    extra: context,
  });
}

/** ``typeof``, but distinguishing an array and ``null`` from a plain object. */
function shapeOf(value: unknown): string {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  return typeof value;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** An empty-but-valid definition, used when the payload isn't usable at all. */
function emptyDefinition(): TIntakeDefinition {
  return { version: DEFINITION_VERSION, pages: [] };
}

/**
 * Coerce one page. Returns the page unchanged (same reference) when its
 * ``elements`` is already an array, so a repair elsewhere in the definition
 * doesn't needlessly reallocate every sibling page.
 */
function normalizePage(value: unknown, repairs: Set<TIntakeDefinitionRepair>): TIntakePage | null {
  if (!isPlainObject(value)) {
    repairs.add("page_not_object");
    return null;
  }
  if (Array.isArray(value.elements)) return value as unknown as TIntakePage;
  repairs.add("page_elements_not_array");
  return { ...value, elements: [] } as unknown as TIntakePage;
}

/**
 * The pure half: coerce ``value`` and report which repairs were needed, with no
 * Sentry/console side effects. {@link normalizeIntakeDefinition} is this plus
 * reporting; tests use this directly to assert coercion without any SDK.
 *
 * A valid input is returned as the identical reference (``repairs`` empty).
 */
export function inspectIntakeDefinition(value: unknown): TIntakeDefinitionInspection {
  const repairs = new Set<TIntakeDefinitionRepair>();

  // An absent definition carries no content — ambiguous "empty", not corruption.
  if (value === null || value === undefined) {
    repairs.add("definition_empty");
    return { definition: emptyDefinition(), repairs: orderRepairs(repairs) };
  }
  // Present but not an object (string / number / array / boolean): corrupt.
  if (!isPlainObject(value)) {
    repairs.add("definition_not_object");
    return { definition: emptyDefinition(), repairs: orderRepairs(repairs) };
  }

  // ``version`` absent is defaulted quietly; present-but-wrong is corrupt.
  if (!("version" in value)) repairs.add("version_missing");
  else if (value.version !== DEFINITION_VERSION) repairs.add("version_invalid");

  const rawPages = value.pages;
  // ``pages`` absent entirely ⇒ empty (the `{}` new-form shape). Present but not
  // an array (object / string / number / null) ⇒ corrupt whatever the path.
  if (!("pages" in value)) {
    repairs.add("definition_empty");
    return {
      definition: { ...value, version: DEFINITION_VERSION, pages: [] } as TIntakeDefinition,
      repairs: orderRepairs(repairs),
    };
  }
  if (!Array.isArray(rawPages)) {
    repairs.add("pages_not_array");
    return {
      definition: { ...value, version: DEFINITION_VERSION, pages: [] } as TIntakeDefinition,
      repairs: orderRepairs(repairs),
    };
  }

  const pages: TIntakePage[] = [];
  let pagesChanged = false;
  for (const rawPage of rawPages) {
    const page = normalizePage(rawPage, repairs);
    if (page === null) {
      pagesChanged = true;
      continue;
    }
    if (page !== rawPage) pagesChanged = true;
    pages.push(page);
  }

  if (repairs.size === 0) {
    // Already valid — hand back the very same object. Consumers memoize on this
    // identity and MobX observes it; cloning here would churn every render.
    return { definition: value as unknown as TIntakeDefinition, repairs: [] };
  }

  return {
    definition: {
      ...value,
      version: DEFINITION_VERSION,
      pages: pagesChanged ? pages : (rawPages as TIntakePage[]),
    } as TIntakeDefinition,
    repairs: orderRepairs(repairs),
  };
}

/** Most-severe-first, so `repairs[0]` is the reported reason. */
function orderRepairs(repairs: Set<TIntakeDefinitionRepair>): TIntakeDefinitionRepair[] {
  return REPAIR_SEVERITY.filter((r) => repairs.has(r));
}

/**
 * **The boundary.** Coerce an untrusted definition payload into a structurally
 * valid {@link TIntakeDefinition}, reporting a repair to Sentry exactly once per
 * ``reason``+``formId`` per session — but only a repair that is *reportable* at
 * this entry point (see {@link TIntakeDefinitionExpectation}). Coercion always
 * happens; alarming is what the expectation gates.
 *
 * Returns the input by reference when it was already valid, and captures
 * nothing on that path.
 */
export function normalizeIntakeDefinition(
  value: unknown,
  context: TIntakeDefinitionContext,
): TIntakeDefinition {
  const { definition, repairs } = inspectIntakeDefinition(value);
  const reason = repairs.find((r) => isReportableRepair(r, context.expectation));
  if (reason === undefined) return definition;

  reportIntakeDefinitionRepair({
    ...context,
    reason,
    repairs,
    definitionType: shapeOf(value),
    pagesType: shapeOf(isPlainObject(value) ? value.pages : undefined),
    pageCount: definition.pages.length,
  });
  return definition;
}

/** Pages on a possibly-malformed definition, without reporting anything. */
function pageCountOf(value: unknown): number {
  if (!isPlainObject(value) || !Array.isArray(value.pages)) return 0;
  return value.pages.length;
}

/**
 * Whether this form *advertises content it should therefore be delivering*.
 *
 * A form that has been published — or whose author has unpublished edits, or
 * which carries a non-empty published snapshot — must have a real definition, so
 * an empty one is corruption. This is precisely Lee's case in the 2026-07-28
 * incident: those forms had published workflows whose snapshots failed to
 * reconstruct, leaving `{}` behind. A form that has never been published and
 * serves `{}` is just a new form nobody has authored yet — normalize it silently.
 *
 * These three fields are author-view-only (absent for non-authors,
 * `types/intake.ts`), so a requester-side fetch of an unauthored form reads as
 * ``"may-be-empty"``. That is the right way to be wrong: a form must be Active to
 * be submittable and activation validates the definition
 * (`IntakeFormSerializer.validate` → `assert_definition_valid`), so a requester
 * meeting an empty definition still gets the visible empty state — we just don't
 * page anyone over a shape we can't prove is broken.
 */
function formRequiresDefinition(form: TIntakeForm): boolean {
  return (
    form.published_at != null ||
    form.has_unpublished_changes === true ||
    pageCountOf(form.published?.definition) > 0
  );
}

/**
 * Normalize a form's ``definition`` (and the ``published`` snapshot's, when the
 * author view carries one). Returns the same form reference when nothing needed
 * repairing, so store assignments stay identity-stable.
 *
 * The expectation is **derived per form** from {@link formRequiresDefinition} —
 * callers don't pass one, because whether empty is legitimate is a property of
 * the individual form's publish state, not of the endpoint that fetched it (one
 * `listForms` page can hold both a corrupt published form and a normal new one).
 */
export function normalizeIntakeForm(
  form: TIntakeForm,
  context: Omit<TIntakeDefinitionContext, "formId" | "expectation">,
): TIntakeForm {
  // A non-object where a form was promised is a whole-payload fault, not a
  // definition one — nothing to normalize, and the caller's own contract breaks
  // first. Pass it through untouched rather than manufacturing a fake form.
  if (form === null || typeof form !== "object") return form;
  const formContext = {
    ...context,
    formId: form.id ?? null,
    expectation: formRequiresDefinition(form)
      ? ("requires-definition" as const)
      : ("may-be-empty" as const),
  };
  const definition = normalizeIntakeDefinition(form.definition, formContext);
  const published = form.published;

  if (!published) {
    return definition === form.definition ? form : { ...form, definition };
  }

  const publishedDefinition = normalizeIntakeDefinition(published.definition, {
    ...formContext,
    source: `${context.source}.published`,
  });
  if (definition === form.definition && publishedDefinition === published.definition) return form;
  return {
    ...form,
    definition,
    published: { ...published, definition: publishedDefinition },
  };
}

/** Normalize every form in a list, preserving the array when nothing changed.
 *  Each form's expectation is derived individually — see {@link normalizeIntakeForm}. */
export function normalizeIntakeForms(
  forms: TIntakeForm[],
  context: Omit<TIntakeDefinitionContext, "formId" | "expectation">,
): TIntakeForm[] {
  if (!Array.isArray(forms)) return [];
  const next = forms.map((form) => normalizeIntakeForm(form, context));
  return next.some((form, i) => form !== forms[i]) ? next : forms;
}

/**
 * Normalize a submission's ``form_detail.definition``.
 *
 * Two rules, and they are not the same rule:
 *
 *  - An **absent** ``definition`` is a legitimate wire shape (the field is
 *    optional; the viewer may not be entitled to it, or the detail GET omitted
 *    it). Left absent, nothing reported.
 *  - A **present** ``definition`` is held to ``"requires-definition"``: this
 *    request already exists, so it was filled in against a real definition, and
 *    an empty one means the definition behind it was lost. That is the incident.
 */
export function normalizeIntakeSubmission(
  submission: TIntakeSubmission,
  context: Omit<TIntakeDefinitionContext, "formId" | "expectation">,
): TIntakeSubmission {
  const formDetail = submission?.form_detail;
  if (!formDetail || formDetail.definition === undefined) return submission;

  const definition = normalizeIntakeDefinition(formDetail.definition, {
    ...context,
    formId: formDetail.id ?? null,
    expectation: "requires-definition",
  });
  if (definition === formDetail.definition) return submission;
  return { ...submission, form_detail: { ...formDetail, definition } };
}

/** Normalize every submission in a list, preserving the array when unchanged. */
export function normalizeIntakeSubmissions(
  submissions: TIntakeSubmission[],
  context: Omit<TIntakeDefinitionContext, "formId" | "expectation">,
): TIntakeSubmission[] {
  if (!Array.isArray(submissions)) return [];
  const next = submissions.map((s) => normalizeIntakeSubmission(s, context));
  return next.some((s, i) => s !== submissions[i]) ? next : submissions;
}
